import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { HasReinforcementPadsOptions } from 'types/assetModel/assetModelHelpers'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { forecastMath } from 'utils/fcMath'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { round } = forecastMath

export interface GeckoSkirtSupportJSON {
  type: GeckoFeatureModelType.SkirtSupport
  name: string
  feature_geometry: {
    diameter: number
    height: number
    y: number
  }
  surface_specs: {
    nominal_thickness: number | undefined
    has_reinforcement_pads: string
  }
}

export const NewGeckoSkirtSupportJSON: GeckoSkirtSupportJSON = {
  type: GeckoFeatureModelType.SkirtSupport,
  name: 'New Skirt Support',
  feature_geometry: {
    diameter: 0,
    height: 0,
    y: 0,
  },
  surface_specs: {
    nominal_thickness: undefined,
    has_reinforcement_pads: HasReinforcementPadsOptions.No,
  },
}

export class GeckoSkirtSupport extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.SkirtSupport
  readonly diameter: GeckoMeasurement
  readonly height: GeckoMeasurement
  readonly y: GeckoMeasurement
  readonly nominalThickness: GeckoMeasurement | undefined
  readonly hasReinforcementPads: HasReinforcementPadsOptions

  private constructor(
    id: string,
    name: string,
    diameter: GeckoMeasurement,
    height: GeckoMeasurement,
    y: GeckoMeasurement,
    nominalThickness: GeckoMeasurement | undefined,
    hasReinforcementPads: HasReinforcementPadsOptions
  ) {
    super(id)
    this.name = name
    this.diameter = diameter
    this.height = height
    this.y = y
    this.nominalThickness = nominalThickness
    this.hasReinforcementPads = hasReinforcementPads
  }

  public static CreateFromJSON(
    componentID: string,
    skirtSupportID: string,
    skirtSupportJSON: GeckoSkirtSupportJSON
  ): GeckoSkirtSupport {
    const featureGeometry = skirtSupportJSON.feature_geometry as Record<
      string,
      number
    >
    const surfaceSpecs = skirtSupportJSON.surface_specs as Record<
      string,
      number | string
    >

    const diameter = GeckoMeasurement.CreateFromValue(
      featureGeometry.diameter as number,
      GeckoMeasurementUnit.Meters
    )
    const height = GeckoMeasurement.CreateFromValue(
      featureGeometry.height as number,
      GeckoMeasurementUnit.Meters
    )
    const y = GeckoMeasurement.CreateFromValue(
      featureGeometry.y as number,
      GeckoMeasurementUnit.Meters
    )
    const nominalThickness =
      surfaceSpecs.nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.nominal_thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const hasReinforcementPads =
      (surfaceSpecs.has_reinforcement_pads as HasReinforcementPadsOptions) ??
      HasReinforcementPadsOptions.No

    const skirtSupportName = skirtSupportJSON.name

    return new GeckoSkirtSupport(
      skirtSupportID,
      skirtSupportName,
      diameter,
      height,
      y,
      nominalThickness,
      hasReinforcementPads
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoSkirtSupportJSON>
  ): Map<string, GeckoSkirtSupport> {
    return Object.keys(recordsJSON).reduce((resultObj, skirtSupportID) => {
      resultObj.set(
        skirtSupportID,
        GeckoSkirtSupport.CreateFromJSON(
          componentID,
          skirtSupportID,
          recordsJSON[skirtSupportID]
        )
      )
      return resultObj
    }, new Map<string, GeckoSkirtSupport>())
  }

  public asJSON(): GeckoSkirtSupportJSON {
    return generateSkirtSupport(
      this.name,
      this.diameter,
      this.height,
      this.y,
      this.nominalThickness,
      this.hasReinforcementPads
    )
  }
}

export function generateSkirtSupport(
  name: string,
  diameter: GeckoMeasurement,
  height: GeckoMeasurement,
  y: GeckoMeasurement,
  nominalThickness: GeckoMeasurement | undefined,
  hasReinforcementPads: HasReinforcementPadsOptions = HasReinforcementPadsOptions.No
): GeckoSkirtSupportJSON {
  const skirtSupportJSON: GeckoSkirtSupportJSON = {
    type: GeckoFeatureModelType.SkirtSupport,
    name: name,
    feature_geometry: {
      diameter: round(diameter.toNumber(GeckoMeasurementUnit.Meters), 5),
      height: round(height.toNumber(GeckoMeasurementUnit.Meters), 5),
      y: round(y.toNumber(GeckoMeasurementUnit.Meters), 5),
    },
    surface_specs: {
      nominal_thickness:
        nominalThickness !== undefined
          ? round(nominalThickness.toNumber(GeckoMeasurementUnit.Meters), 5)
          : undefined,
      has_reinforcement_pads: hasReinforcementPads,
    },
  }

  return skirtSupportJSON
}

export const SkirtSupportFormFields = (
  skirtSupportJSON: GeckoSkirtSupportJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Skirt Support Name',
      value: skirtSupportJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'diameter',
      label: 'Diameter',
      value: skirtSupportJSON.feature_geometry.diameter,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: skirtSupportJSON.feature_geometry.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: skirtSupportJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'nominal_thickness',
      label: 'Nominal Thickness',
      value: skirtSupportJSON.surface_specs.nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'has_reinforcement_pads',
      label: 'Has Reinforcement Pads',
      value: skirtSupportJSON.surface_specs.has_reinforcement_pads,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: getDropdownOptionsForEnum(HasReinforcementPadsOptions),
    },
  ]
}

export const SkirtSupportJSONFromFormFields = (formFields: FormField[]) => {
  const nominalThickness = getFormFieldByDataKey(
    formFields,
    'nominal_thickness'
  )
  const hasReinforcementPads = getFormFieldByDataKey(
    formFields,
    'has_reinforcement_pads'
  )

  return generateSkirtSupport(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'diameter')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    nominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          nominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    hasReinforcementPads?.value as HasReinforcementPadsOptions
  )
}

export const SkirtSupportFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.SkirtSupport,
  title: 'Skirt Supports',
  singularName: 'Skirt Support',
  fields: [
    'name',
    'diameter',
    'height',
    'y',
    'nominal_thickness',
    'has_reinforcement_pads',
  ],
  sortBy: [{ fieldName: 'y', order: 'asc' }],
}
